<!--
  FilterInput.vue

  Form input for filter-kind fields. Renders the filter's description as
  a button that opens FilterEditorModal; emits route through `useField`.

  Component owns the open/closed state; the modal itself is stateless
  over modelValue.
-->
<template>
  <div class="filter-input" :class="[`is-${size}`]">
    <span v-if="readonly" class="text-7 is-elided">
      {{ description || "No filter" }}
    </span>
    <button
      v-else
      type="button"
      class="btn is-fullwidth is-justify-start"
      :disabled="disabled"
      @click="modalOpen = true"
    >
      <span class="icon is-small"><font-awesome-icon icon="filter" /></span>
      <span class="is-elided">{{ description || "Edit filter…" }}</span>
    </button>

    <FilterEditorModal
      :is-open="modalOpen"
      :model-value="modelValue"
      :target-type="targetType"
      :title="field?.label ? `Edit ${field.label}` : 'Edit filter'"
      @close="modalOpen = false"
      @update:model-value="handleInput"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue"
import type { AugerField } from "@auger/core/fields"
import { useField } from "@auger/ui/fields/useField"
import FilterEditorModal from "@/components/filters/FilterEditorModal.vue"
import { describeFilter, type FilterFieldDef, type SerializedFilter } from "@/composables/useFilter"
import { getTypeSchema } from "@/stores/types"

const props = withDefaults(
  defineProps<{
    modelValue?: SerializedFilter | null
    field: AugerField
    readonly?: boolean
    disabled?: boolean
    size?: "small" | "normal" | "medium" | "large"
  }>(),
  {
    modelValue: null,
    readonly: false,
    disabled: false,
    size: "normal",
  }
)

const emit = defineEmits<{
  "update:modelValue": [value: unknown]
}>()

const { handleInput } = useField(props.field, emit)

const modalOpen = ref(false)

const targetType = computed(() => props.field?.recordType ?? null)

// One-time warning per record type when the types store hasn't surfaced a
// schema for a configured record type. Silent fallback would make it look
// like the field has no fields — most often that's a race with types-
// store hydration or a typo in `field.recordType`.
const warnedSubtypes = new Set<string>()
const targetFields = computed<FilterFieldDef[]>(() => {
  if (!targetType.value) return []
  const schema = getTypeSchema(targetType.value)
  if (!schema && !warnedSubtypes.has(targetType.value)) {
    warnedSubtypes.add(targetType.value)
    console.warn(
      `FilterInput: no schema for record type "${targetType.value}". ` +
        `Either the types store hasn't hydrated yet, or the field.recordType is invalid.`
    )
  }
  return (schema?.fields ?? []) as FilterFieldDef[]
})
const description = computed(() => describeFilter(props.modelValue, targetFields.value))
</script>

<style scoped>
.filter-input {
  width: 100%;
}
.is-elided {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}
.is-justify-start {
  justify-content: flex-start;
  text-align: left;
}
</style>
